import React from 'react';
import { Link } from 'react-router-dom';

/**
 * PageBlocks - Rendu des blocs d'une page éditoriale
 *
 * Le corps d'une page est un tableau de blocs typés, pas du HTML de mise en
 * page. Chaque type de bloc a son composant : la mise en forme vit ici, dans
 * du JSX, et non dans du balisage stocké en base.
 *
 * Ce choix vient d'une mesure faite sur le serveur : getSafeHTML retire
 * style=, class= et data-type=, et supprime <table> et <img>. Du HTML de mise
 * en page serait donc aplati à l'enregistrement. En le remplaçant par des
 * blocs, la charte reste tenue par les composants et un administrateur ne
 * peut pas la casser.
 *
 * Ajouter un type de bloc suppose de l'ajouter AUSSI à BLOC_TYPES dans
 * backend/cfc/cabaza_pages.cfc, sans quoi il sera refusé à l'écriture.
 */

import TarifBloc from './blocs/TarifBloc';
import CapturesBloc from './blocs/CapturesBloc';
import FaqBloc from './blocs/FaqBloc';
import ComparatifBloc from './blocs/ComparatifBloc';
import ArgumentsBloc from './blocs/ArgumentsBloc';
import BandeauCtaBloc from './blocs/BandeauCtaBloc';
import EtapesBloc from './blocs/EtapesBloc';
import ChiffresBloc from './blocs/ChiffresBloc';
import TemoignagesBloc from './blocs/TemoignagesBloc';
import GrilleModulesBloc from './blocs/GrilleModulesBloc';
import ModuleLieBloc from './blocs/ModuleLieBloc';
import OngletsBloc from './blocs/OngletsBloc';
import GrilleTarifsBloc from './blocs/GrilleTarifsBloc';
import LogosBloc from './blocs/LogosBloc';
import AccueilConnexionBloc from './blocs/AccueilConnexionBloc';
import { iconeDe, couleurDe, alignementDe, colonnesDe, espaceDe } from './blocs/apparence';

const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

/**
 * Rend un champ de texte riche.
 *
 * dangerouslySetInnerHTML se limite désormais à CE SEUL cas : un champ
 * `contenu` déjà passé par getSafeHTML à l'écriture (cabaza_pages.cfc,
 * SanitizeRichText), qui n'y laisse survivre que <p>, <strong>, <em>, <ul>,
 * <li>, <h*> sans attribut et <a href> relatif ou https:. Ce n'est plus une
 * page entière qui est injectée, mais un fragment de texte courant — la
 * surface est bien plus étroite qu'avec du HTML de mise en page.
 *
 * Ne pas reproduire ce motif sur du contenu qui n'aurait pas traversé
 * getSafeHTML à l'écriture.
 */
const RichText = ({ html, className }) => {
    if (!html) {
        return null;
    }

    return (
        <div
            className={className}
            data-testid="bloc-richtext"
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
};

/**
 * Le hero d'une page de présentation.
 *
 * Quatre ajouts FACULTATIFS par rapport à la version d'origine : un badge,
 * un sous-titre en dégradé, et deux actions. Sans eux, le rendu est
 * exactement celui d'avant — les 21 pages déjà en base ne bougent pas.
 *
 * Le sous-titre passe en dégradé dès qu'une action est présente : c'est le
 * signe qu'on est sur une page qui vend, et non sur une simple page de
 * documentation. Le vocabulaire vient de LandingPage.
 */
/**
 * Le titre, suivi de son mot d'accent en violet — « Choisissez votre
 * formule ». L'accent est un champ à part plutôt qu'un balisage dans le
 * titre : le titre reste du texte brut, échappé par React.
 */
const TitreHero = ({ bloc }) => (
    <>
        {bloc.titre}
        {bloc.titreAccent && (
            <>{' '}<span className="text-cabaza-purple">{bloc.titreAccent}</span></>
        )}
    </>
);

/**
 * LA TAILLE GRANDE : le hero de PricingPage, classe pour classe.
 *
 * C'est le titre de la page — un h1, là où le hero ordinaire pose un h2
 * sous le titre de la carte. Il porte sa propre largeur (max-w-7xl) et ses
 * marges (py-16) : il est fait pour la mise en page pleine largeur, où
 * aucune carte ne l'encadre.
 */
const HeroGrand = ({ bloc }) => {
    const align = alignementDe(bloc.alignement);

    return (
        <section
            className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 ${align.conteneur}`.trim()}
            data-testid="bloc-hero"
        >
            {bloc.titre && (
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                    <TitreHero bloc={bloc} />
                </h1>
            )}
            {bloc.sousTitre && (
                <p className={`text-xl text-gray-600 max-w-2xl ${align.conteneur ? 'mx-auto' : ''}`.trim()}>
                    {bloc.sousTitre}
                </p>
            )}
        </section>
    );
};

/**
 * LA TAILLE TITRE : le haut de InstallAppPage, classe pour classe.
 *
 * Comme la taille grande, c'est le titre de la page — un h1 —, mais dans la
 * colonne de 896 px de la mise en page « colonne », sans largeur ni marges
 * propres. Le hero ordinaire n'y suffisait pas : un h2 plus petit, un
 * sous-titre plus pale (gray-500) et une marge de mb-10 au lieu de mb-12.
 *
 * mb-12 est donc son espace par defaut, celui du repli ; le choix
 * « espace » le remplace comme pour les autres blocs.
 */
const HeroTitre = ({ bloc }) => {
    const Icone = iconeDe(bloc.icone);
    const couleur = couleurDe(bloc.couleur);
    const align = alignementDe(bloc.alignement);

    return (
        <section
            className={`${espaceDe(bloc.espace, 'mb-12')} ${align.conteneur}`.trim()}
            data-testid="bloc-hero"
        >
            {/* La pastille du hero ordinaire : c'est deja celle du repli. */}
            {Icone && (
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6 ${couleur.pastille} ${align.pastille}`}>
                    <Icone className={`w-10 h-10 ${couleur.icone}`} />
                </div>
            )}
            {bloc.titre && (
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    <TitreHero bloc={bloc} />
                </h1>
            )}
            {bloc.sousTitre && (
                <p className={`text-lg text-gray-600 ${align.conteneur ? 'max-w-2xl mx-auto' : ''}`.trim()}>
                    {bloc.sousTitre}
                </p>
            )}
        </section>
    );
};

const HeroBloc = ({ bloc }) => {
    if (bloc.taille === 'grande') {
        return <HeroGrand bloc={bloc} />;
    }

    if (bloc.taille === 'titre') {
        return <HeroTitre bloc={bloc} />;
    }

    const aUneAction = Boolean(bloc.actionTexte && bloc.actionLien);
    const aUneSecondaire = Boolean(bloc.actionSecondaireTexte && bloc.actionSecondaireLien);

    // L'ICONE EN TETE, dans sa pastille : c'est ce qui manquait le plus au
    // rendu par blocs face a la page d'installation d'origine.
    const Icone = iconeDe(bloc.icone);
    const couleur = couleurDe(bloc.couleur);
    const align = alignementDe(bloc.alignement);

    return (
        <section className={`${espaceDe(bloc.espace, 'mb-10')} ${align.conteneur}`} data-testid="bloc-hero">
            {Icone && (
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6 ${couleur.pastille} ${align.pastille}`}>
                    <Icone className={`w-10 h-10 ${couleur.icone}`} />
                </div>
            )}

            {bloc.image && (
                <img
                    src={`${FILES_BASE_URL}/${bloc.image}`}
                    alt=""
                    className="w-full h-56 md:h-72 object-cover rounded-2xl mb-6"
                />
            )}

            {bloc.badge && (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 border border-green-200 mb-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-600" />
                    <span className="text-xs font-semibold text-green-700">{bloc.badge}</span>
                </div>
            )}

            {bloc.titre && (
                <h2 className={`font-bold text-gray-900 mb-2 ${aUneAction ? 'text-3xl md:text-4xl font-extrabold leading-tight' : 'text-2xl md:text-3xl'}`}>
                    <TitreHero bloc={bloc} />
                </h2>
            )}

            {bloc.sousTitre && (
                <p className={aUneAction
                    ? `text-lg md:text-xl font-semibold bg-gradient-to-r from-cabaza-purple to-cabaza-pink bg-clip-text text-transparent max-w-2xl ${align.conteneur ? 'mx-auto' : ''}`
                    : `text-lg text-gray-500 ${align.conteneur ? 'max-w-2xl mx-auto' : ''}`}>
                    {bloc.sousTitre}
                </p>
            )}

            {(aUneAction || aUneSecondaire) && (
                <div className="flex flex-wrap gap-3 mt-6">
                    {aUneAction && (
                        <Link
                            to={bloc.actionLien}
                            className="px-6 py-3 rounded-xl bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white font-semibold hover:opacity-90 transition-opacity"
                        >
                            {bloc.actionTexte}
                        </Link>
                    )}
                    {aUneSecondaire && (
                        <Link
                            to={bloc.actionSecondaireLien}
                            className="px-5 py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:border-gray-300 transition-colors"
                        >
                            {bloc.actionSecondaireTexte}
                        </Link>
                    )}
                </div>
            )}
        </section>
    );
};

/**
 * La typographie des pages d'origine, portée par les DESCENDANTS.
 *
 * `prose` ne fait rien ici : @tailwindcss/typography n'est pas installé, et
 * le preflight de Tailwind v4 ramène un <h2> à la taille du texte courant et
 * retire les puces d'un <ul>. Les pages de repli s'en sortent en posant une
 * classe sur chaque balise ; le texte riche ne le peut pas, getSafeHTML
 * retirant l'attribut class. Les classes sont donc posées sur le conteneur,
 * recopiées de LegalPage, PrivacyPage et CookiesPage.
 *
 * Le plugin aurait été plus court, mais il aurait changé d'un coup le rendu
 * de toutes les pages, repli compris.
 */
const TYPO_REPLI = '[&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-gray-900 [&_h2]:mb-4 '
    + '[&_h3]:font-semibold [&_h3]:text-gray-900 [&_h3]:mb-2 '
    + '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mt-2 [&_ul]:space-y-1 '
    + '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mt-2 [&_ol]:space-y-1 '
    + '[&_p+p]:mt-2 [&_a]:text-cabaza-purple [&_a]:underline';

const TexteBloc = ({ bloc }) => (
    <section className="mb-8" data-testid="bloc-texte">
        <RichText html={bloc.contenu} className={`prose prose-purple max-w-none text-gray-600 ${TYPO_REPLI}`} />
    </section>
);

/**
 * Les tons d'un encadré — une LISTE FERMÉE, jamais une couleur saisie.
 *
 * Le ton vient de la base, donc d'un champ éditable. Laisser passer une
 * couleur libre aurait deux conséquences : la cohérence visuelle du site
 * partirait page par page, et la valeur atterrirait telle quelle dans un
 * nom de classe — une chaîne arbitraire venue de la base dans du CSS.
 *
 * Les classes sont écrites EN TOUTES LETTRES : Tailwind analyse le source
 * à la compilation, et une classe construite par concaténation
 * (`bg-${ton}-50`) serait absente du CSS produit.
 */
const TONS_ENCADRE = {
    neutre:  { fond: 'bg-purple-50 border-purple-100', titre: 'text-purple-900', texte: 'prose-purple text-purple-800' },
    alerte:  { fond: 'bg-red-50 border-red-200',       titre: 'text-red-900',    texte: 'prose-red text-red-800' },
    succes:  { fond: 'bg-green-50 border-green-200',   titre: 'text-green-900',  texte: 'prose-green text-green-800' },
    info:    { fond: 'bg-blue-50 border-blue-200',     titre: 'text-blue-900',   texte: 'prose-blue text-blue-800' }
};

/**
 * Le ton « section » : pas une boîte, une section de page juridique.
 *
 * Les quatre pages juridiques ont été mises en base un encadré par section.
 * En ton neutre, elles s'affichaient en boîtes violettes à texte réduit, là
 * où LegalPage pose une section blanche sous un intertitre. Ce rendu recopie
 * leur JSX classe pour classe ; `strong` y prend le `font-medium
 * text-gray-900` du nom de l'éditeur des mentions légales.
 */
const SectionBloc = ({ bloc }) => (
    <section className="mb-8" data-testid="bloc-encadre">
        {bloc.titre && (
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{bloc.titre}</h2>
        )}
        <RichText
            html={bloc.contenu}
            className={`max-w-none text-gray-600 ${TYPO_REPLI} [&_strong]:font-medium [&_strong]:text-gray-900`}
        />
    </section>
);

const EncadreBloc = ({ bloc }) => {
    if (bloc.ton === 'section') {
        return <SectionBloc bloc={bloc} />;
    }

    // Un ton inconnu retombe sur neutre : la valeur n'est jamais employée
    // pour fabriquer une classe.
    const ton = TONS_ENCADRE[bloc.ton] || TONS_ENCADRE.neutre;

    return (
        <aside
            className={`mb-8 px-5 py-4 rounded-xl border ${ton.fond}`}
            data-testid="bloc-encadre"
        >
            {bloc.titre && (
                <h3 className={`font-semibold mb-2 ${ton.titre}`}>{bloc.titre}</h3>
            )}
            <RichText html={bloc.contenu} className={`prose max-w-none text-sm ${ton.texte}`} />
        </aside>
    );
};

const BoutonBloc = ({ bloc }) => {
    if (!bloc.href || !bloc.libelle) {
        return null;
    }

    const className = 'inline-flex items-center px-5 py-2.5 rounded-xl bg-gradient-to-br from-cabaza-purple to-cabaza-pink text-white font-medium hover:opacity-90 transition-opacity';

    // Un lien interne passe par le routeur pour éviter un rechargement complet ;
    // un lien externe (https:, mailto:) reste une ancre classique. Les href ont
    // été restreints au relatif, https: et mailto: à l'écriture.
    const isInternal = bloc.href.startsWith('/');

    return (
        <div className="mb-8" data-testid="bloc-bouton">
            {isInternal ? (
                <Link to={bloc.href} className={className}>{bloc.libelle}</Link>
            ) : (
                <a href={bloc.href} className={className} rel="noopener noreferrer">
                    {bloc.libelle}
                </a>
            )}
        </div>
    );
};

const GrilleCartesBloc = ({ bloc }) => {
    const cartes = Array.isArray(bloc.cartes) ? bloc.cartes : [];

    if (cartes.length === 0) {
        return null;
    }

    // LE NOMBRE DE COLONNES SUIT LE NOMBRE DE CARTES quand on ne le choisit
    // pas : trois cartes tiennent en trois colonnes. La grille etait figee a
    // deux, ce qui cassait la rangee de trois avantages de la page
    // d'installation.
    const grille = colonnesDe(bloc.colonnes, cartes.length);
    const align = alignementDe(bloc.alignement);

    return (
        <section className={`${espaceDe(bloc.espace, 'mb-8')} grid gap-4 ${grille}`} data-testid="bloc-grilleCartes">
            {cartes.map((carte, index) => {
                const Icone = iconeDe(carte.icone);
                const couleur = couleurDe(carte.couleur);

                return (
                    /* LA CARTE DE LA PAGE D'ORIGINE, AU DETAIL PRES.
                       `border` seul prend la bordure par defaut de Tailwind —
                       un gris franc — et sans ombre : la carte devenait un
                       rectangle dessine au trait, la ou la page d'origine pose
                       une carte blanche qui se detache du fond gris. La classe
                       exacte de `InstallAppPage` est reprise telle quelle :
                       bordure `gray-100`, tres claire, et `shadow-sm`. */
                    <div key={index} className={`bg-white rounded-xl p-5 shadow-sm border border-gray-100 ${align.conteneur}`}>
                        {Icone && (
                            <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg mb-3 ${couleur.pastille} ${align.pastille}`}>
                                <Icone className={`w-5 h-5 ${couleur.icone}`} />
                            </div>
                        )}
                        {carte.titre && (
                            <h3 className="font-semibold text-gray-900 mb-1">{carte.titre}</h3>
                        )}
                        {/* text-sm ET NON prose-sm : @tailwindcss/typography
                            n'est pas installe, prose-sm ne faisait rien et le
                            texte sortait en 16 px, la ou le repli ecrit en
                            14 px. [&_p+p]:mt-2 garde l'ecart entre deux
                            paragraphes, que TYPO_REPLI pose aussi. */}
                        <RichText html={carte.texte} className="text-sm text-gray-600 [&_p+p]:mt-2" />
                    </div>
                );
            })}
        </section>
    );
};

const BLOC_COMPONENTS = {
    hero: HeroBloc,
    texte: TexteBloc,
    encadre: EncadreBloc,
    bouton: BoutonBloc,
    grilleCartes: GrilleCartesBloc,
    tarif: TarifBloc,
    captures: CapturesBloc,
    faq: FaqBloc,
    comparatif: ComparatifBloc,
    arguments: ArgumentsBloc,
    bandeauCta: BandeauCtaBloc,
    etapes: EtapesBloc,
    onglets: OngletsBloc,
    chiffres: ChiffresBloc,
    temoignages: TemoignagesBloc,
    // Le seul bloc qui lit la base au lieu de porter son contenu : les 21
    // modules recopies deviendraient faux des qu'on en ajoute un.
    grilleModules: GrilleModulesBloc,
    // La suggestion editoriale d'un module complementaire : le choix est
    // humain, mais le nom et le resume restent lus en base.
    moduleLie: ModuleLieBloc,
    // La tarification d'origine : sa grille (formules lues en base) et sa
    // bande de moyens de paiement. Faits pour la mise en page pleine largeur.
    grilleTarifs: GrilleTarifsBloc,
    logos: LogosBloc,
    // Le hero de la page d'accueil, formulaire de connexion compris. Le seul
    // bloc qui porte une action de l'application et non un simple lien : le
    // formulaire est partage avec LandingPage (FormulaireConnexion).
    accueilConnexion: AccueilConnexionBloc
};

/**
 * Rend la suite des blocs d'une page.
 *
 * Un bloc de type inconnu est ignoré silencieusement plutôt que de faire
 * échouer la page entière : le backend refuse déjà ces blocs à l'écriture,
 * donc un type inconnu ici signale une base plus récente que le frontend
 * (déploiement partiel), pas une donnée hostile.
 *
 * @param {Array} blocs - Tableau de blocs typés
 */
const PageBlocks = ({ blocs, langue = 'fr' }) => {
    if (!Array.isArray(blocs) || blocs.length === 0) {
        return null;
    }

    return (
        <>
            {blocs.map((bloc, index) => {
                const Composant = BLOC_COMPONENTS[bloc?.type];

                if (!Composant) {
                    return null;
                }

                // La langue sert au bloc tarif : le formatage monetaire
                // en depend, et c'est la seule donnee qu'il ne lit pas en base.
                return <Composant key={index} bloc={bloc} langue={langue} />;
            })}
        </>
    );
};

export default PageBlocks;
